import {faSortAlt} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {DragDropContext, DropResult} from '@hello-pangea/dnd';
import {MenuItem, MenuList} from '@mui/material';
import Box from '@mui/material/Box';
import {FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Indicator} from '../indicator-types';
import {Drag} from './DragNDrop/Drag';
import {Drop} from './DragNDrop/Drop';

export const IndicatorsOrder: FC<{
  indicators: Indicator[];
  changeOrder: (indicators: Indicator[]) => void;
}> = ({indicators: initialIndicators, changeOrder}) => {
  const {t} = useTranslation();
  const [localIndicators, setLocalIndicators] = useState(initialIndicators);

  useEffect(() => {
    setLocalIndicators(initialIndicators);
  }, [initialIndicators]);

  const reorder = (list: Indicator[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const getSortedIndicators = () => {
    return [...localIndicators].sort((a, b) => {
      return a.order - b.order;
    });
  };

  const onDragEnd = (result: DropResult) => {
    const {source, destination} = result;
    if (destination === null || destination.index === source.index) {
      return;
    }
    const newOrder = [...reorder(getSortedIndicators(), source.index, destination.index)];
    const orderWithPosition = newOrder.map((indicator: Indicator, index) => {
      return {...indicator, order: index};
    });

    setLocalIndicators(orderWithPosition);
    changeOrder(orderWithPosition);
  };

  const dragJsx = (indicator: Indicator) => (
    <MenuItem
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
      key={indicator.uuid}
      style={{border: 'solid 1px #C5CCD2'}}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1}}>
        <FontAwesomeIcon icon={faSortAlt} size={'xs'} color={'primary'} />
        {indicator.defaultDashboard ? (
          <span style={{color: '#1976d2'}}>{indicator.defaultDashboard + ' ·'}</span>
        ) : null}
        {indicator.label}
      </Box>
    </MenuItem>
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Drop droppableId="DROP" type="fixed">
        <MenuList
          sx={{
            width: '100%',
            position: 'relative',
            '& .draggable-content': {
              width: '100% !important',
              maxWidth: 'none !important',
              left: 'auto !important',
              top: 'auto !important',
              marginBottom: '4px',
              overflow: 'hidden',
            },
          }}
        >
          {getSortedIndicators().map((indicator: Indicator, index: number) => (
            <Drag
              key={indicator.uuid}
              draggableId={`${indicator.uuid}`}
              draggableElement={dragJsx(indicator)}
              index={index}
              className="draggable-content"
            >
              <div />
            </Drag>
          ))}
        </MenuList>
      </Drop>
    </DragDropContext>
  );
};
